import { useEffect, useMemo, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useAutoSave } from '@hooks/useAutoSave';
import { Box, Button, TextField } from '@mui/material';

import type {
  GeneratedIntakeField,
  GeneratedIntakeGroupHelp,
  IntakeFieldValue,
  IntakePayerRosterEntry,
} from '../../types';

import { GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { IntakePageFieldInput } from './IntakePageFieldInput';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { cardSx, chipSx, groupTitleSx, wf } from './tokens';
import type { PayerRow } from './types';
import { useGroupHelpPanel } from './useGroupHelpPanel';

interface IntakePayersPageProps {
  payerRows: PayerRow[];
  initialRoster: IntakePayerRosterEntry[];
  fields: GeneratedIntakeField[];
  groups?: GeneratedIntakeGroupHelp[];
  draftValues: Record<string, IntakeFieldValue>;
  acknowledgedValues: Record<string, IntakeFieldValue>;
  onDraftChange: (field: GeneratedIntakeField, value: IntakeFieldValue) => void;
  onPersistField: (field: GeneratedIntakeField, value: IntakeFieldValue) => Promise<void>;
  onPersistRoster: (rows: IntakePayerRosterEntry[]) => Promise<void>;
  onContinue: () => void;
  onBack?: () => void;
  nextLabel: string;
  onHelpAction?: (action: 'message_specialist') => void;
  disabledReasons?: Record<string, string>;
  saveMode?: 'autosave' | 'explicit';
  isSaving?: boolean;
  saveErrorCount?: number;
  onSaveChanges?: () => Promise<void> | void;
  saveDisabled?: boolean;
}

const EMPTY_ROW: IntakePayerRosterEntry = { payer_name: '', payer_id: '' };

function rowsEqual(left: IntakePayerRosterEntry[], right: IntakePayerRosterEntry[]): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function rowsAreComplete(rows: IntakePayerRosterEntry[]): boolean {
  return rows.every(row => Boolean(row.payer_name.trim()) && Boolean(row.payer_id.trim()));
}

export function IntakePayersPage({
  payerRows,
  initialRoster,
  fields,
  groups = [],
  draftValues,
  acknowledgedValues,
  onDraftChange,
  onPersistField,
  onPersistRoster,
  onContinue,
  onBack,
  nextLabel,
  onHelpAction = () => undefined,
  disabledReasons = {},
  saveMode = 'autosave',
  isSaving = false,
  saveErrorCount = 0,
  onSaveChanges,
  saveDisabled = false,
}: IntakePayersPageProps) {
  const [rosterRows, setRosterRows] = useState<IntakePayerRosterEntry[]>(() =>
    initialRoster.length > 0 ? initialRoster : [EMPTY_ROW],
  );
  const [acknowledgedRoster, setAcknowledgedRoster] = useState(initialRoster);
  const previousInitialRoster = useRef(initialRoster);
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } = useGroupHelpPanel(groups);
  const drawerId = 'intake-payers-help';
  const rosterGroup = groups.find(group => group.group_id === 'I13');
  const availityGroup = groups.find(group => group.group_id === 'I23');
  const availityField = fields.find(field => field.group_id === 'I23');

  const rosterDirty = useMemo(() => {
    const persistedDraft = rosterRows.filter(row => row.payer_name || row.payer_id);
    return !rowsEqual(persistedDraft, acknowledgedRoster);
  }, [acknowledgedRoster, rosterRows]);

  useEffect(() => {
    if (rowsEqual(initialRoster, previousInitialRoster.current) || rosterDirty) return;
    previousInitialRoster.current = initialRoster;
    setAcknowledgedRoster(initialRoster);
    setRosterRows(initialRoster.length > 0 ? initialRoster : [{ ...EMPTY_ROW }]);
  }, [initialRoster, rosterDirty]);
  const rosterComplete = rowsAreComplete(rosterRows);
  const availityDirty = Boolean(
    availityField &&
    draftValues[availityField.field_id] !== acknowledgedValues[availityField.field_id],
  );
  const persistRoster = async (rows: IntakePayerRosterEntry[]) => {
    await onPersistRoster(rows);
    setAcknowledgedRoster(rows);
  };
  const { saveImmediately, triggerAutoSave, status, lastSavedAt } = useAutoSave<
    IntakePayerRosterEntry[]
  >({
    enabled: saveMode === 'autosave',
    debounceMs: 800,
    flushOnUnmount: saveMode === 'autosave',
    onSave: persistRoster,
  });

  const changeRow = (index: number, patch: Partial<IntakePayerRosterEntry>) => {
    const next = rosterRows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row,
    );
    setRosterRows(next);
    if (saveMode === 'autosave' && rowsAreComplete(next)) triggerAutoSave(next);
  };

  const saveRosterOnBlur = () => {
    if (saveMode === 'autosave' && rosterDirty && rosterComplete) {
      saveImmediately(rosterRows);
    }
  };

  const handleExplicitSave = async () => {
    if (rosterDirty && rosterComplete) await persistRoster(rosterRows);
    await onSaveChanges?.();
  };

  return (
    <>
      <IntakePageHeader
        title="Payers"
        sub="Review your payer setup and fill in the details below."
      />

      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Box sx={groupTitleSx}>Your payer list</Box>
          <Box sx={{ ...chipSx('mut'), fontSize: '10.5px' }}>
            from Key questions — clinic admin can edit
          </Box>
        </Box>
        <Box sx={{ color: wf.muted, fontSize: '12.5px', lineHeight: 1.7 }}>
          {payerRows.length > 0
            ? payerRows.map(row => row.displayName).join(' · ')
            : 'No payers selected yet.'}
        </Box>
      </Box>

      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <Box sx={groupTitleSx}>Payers you already bill (payer roster)</Box>
          {rosterGroup?.help_panel && (
            <GroupHelpTrigger
              groupName={rosterGroup.group_name}
              controlsId={drawerId}
              expanded={selectedGroupId === 'I13'}
              onOpen={opener => openGroup('I13', opener)}
            />
          )}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
            gap: '8px',
            alignItems: 'start',
          }}
        >
          {rosterRows.map((row, index) => (
            <Box key={index} sx={{ display: 'contents' }}>
              <TextField
                size="small"
                label="Payer name"
                value={row.payer_name}
                required
                slotProps={{ htmlInput: { 'aria-label': `Payer name ${index + 1}` } }}
                onChange={event => changeRow(index, { payer_name: event.target.value })}
                onBlur={saveRosterOnBlur}
              />
              <TextField
                size="small"
                label="Payer ID"
                value={row.payer_id}
                required
                slotProps={{ htmlInput: { 'aria-label': `Payer ID ${index + 1}` } }}
                onChange={event => changeRow(index, { payer_id: event.target.value })}
                onBlur={saveRosterOnBlur}
              />
              <Button
                size="small"
                color="inherit"
                aria-label={`Remove payer ${index + 1}`}
                disabled={rosterRows.length === 1 && !row.payer_name && !row.payer_id}
                onClick={() => {
                  const next = rosterRows.filter((_, rowIndex) => rowIndex !== index);
                  const displayRows = next.length > 0 ? next : [EMPTY_ROW];
                  setRosterRows(displayRows);
                  if (saveMode === 'autosave' && rowsAreComplete(next)) saveImmediately(next);
                }}
              >
                Remove
              </Button>
            </Box>
          ))}
        </Box>
        {!rosterComplete && rosterDirty && (
          <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
            Enter both the payer name and payer ID for every row.
          </Box>
        )}
        <Button
          size="small"
          color="inherit"
          onClick={() => setRosterRows(previous => [...previous, { ...EMPTY_ROW }])}
          sx={{ mt: 0.5 }}
        >
          Add another payer
        </Button>
        {saveMode === 'autosave' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', minHeight: 28 }}>
            <AutosaveStatus
              status={status}
              isDirty={rosterDirty}
              lastSavedAt={lastSavedAt}
              messages={{ error: 'Could not save payer roster' }}
              verbose
            />
          </Box>
        )}
        <Box
          sx={{
            mt: 0.5,
            p: '7px 10px',
            borderRadius: '6px',
            bgcolor: wf.surfaceAlt,
            color: wf.muted,
            fontSize: '11.5px',
          }}
        >
          The payer ID tells us exactly which plan you mean — find it in your current EHR or
          clearinghouse payer list.
        </Box>
      </Box>

      {availityField && (
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Box sx={groupTitleSx}>Availity access</Box>
            {availityGroup?.help_panel && (
              <GroupHelpTrigger
                groupName={availityGroup.group_name}
                controlsId={drawerId}
                expanded={selectedGroupId === 'I23'}
                onOpen={opener => openGroup('I23', opener)}
              />
            )}
          </Box>
          <Box sx={{ color: wf.muted, fontSize: '12.5px', mb: 0.5 }}>{availityField.help_text}</Box>
          <IntakePageFieldInput
            field={{ ...availityField, help_text: '' }}
            draftValue={draftValues[availityField.field_id] ?? false}
            acknowledgedValue={acknowledgedValues[availityField.field_id] ?? false}
            onDraftChange={onDraftChange}
            onPersist={onPersistField}
            disabled={Boolean(disabledReasons[availityField.field_id])}
            disabledReason={disabledReasons[availityField.field_id]}
            saveMode={saveMode}
            showStatus={false}
          />
          <Box sx={{ ...chipSx('pend'), mt: 0.25 }}>exact steps pending confirmation</Box>
        </Box>
      )}

      {saveErrorCount > 0 && (
        <Box sx={{ color: 'error.main', fontSize: '0.8rem' }}>
          {saveErrorCount} change{saveErrorCount === 1 ? '' : 's'} could not be saved.
        </Box>
      )}

      <IntakePageFooter
        primaryLabel={nextLabel}
        onPrimary={onContinue}
        onBack={onBack}
        saveLabel={saveMode === 'explicit' ? (isSaving ? 'Saving…' : 'Save changes') : undefined}
        onSave={saveMode === 'explicit' ? () => void handleExplicitSave() : undefined}
        saveDisabled={
          saveMode === 'explicit' &&
          (isSaving ||
            (rosterDirty && !rosterComplete) ||
            (saveDisabled && !rosterDirty && !availityDirty))
        }
      />
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
    </>
  );
}
